import { ReactElement, ChangeEvent } from 'react';
import { Stack, Typography, Checkbox, Tabs, Tab, TextField } from '@mui/material';
import { usersProvider } from '@/providers';
import { FormButtons } from '@/components';
import { DownPaymentSearch } from '@/components/uiComponents/DownPaymentSearch';
import { KeywordSearch } from '@/components/uiComponents/KeywordSearch';
import { FormSelectWithFetch } from '@/components/uiComponents/FormSelect';
import styles from './MoreFiltersDialog.module.scss';
import { NumericFormat } from 'react-number-format';
import type { CreatorType } from '../filterTypes';

type FiltersBlockSize = 'large' | 'big' | 'middle' | 'small' | 'little' | 'circle';
type FilterOption<T> = {
  label: string;
  value: T;
};
type FiltersBlockCommonProps = {
  icon?: ReactElement;
  title: string;
  dataQa?: string;
};
type FilterButtonClickHandler<T> = {
  bivarianceHack(value: T, name?: string): void;
}['bivarianceHack'];
type ButtonFiltersBlockProps<T extends string | boolean> = FiltersBlockCommonProps & {
  type: 'button';
  name: string;
  size: FiltersBlockSize;
  options: FilterOption<T>[];
  onClick: FilterButtonClickHandler<T>;
  activeButton?: T | T[];
  propertyTypeTab?: number;
  setPropertyTypeTab?: (value: number) => void;
};
type DoubleButtonFiltersBlockProps = FiltersBlockCommonProps & {
  type: 'doubleButton';
  name: 'handover';
  size: FiltersBlockSize;
  subTitle: [string, string];
  options: {
    quarters: FilterOption<string>[];
    years: FilterOption<string>[];
  }[];
  onClick: (value: string, name?: string) => void;
  activeButton?: string;
  activeButtonYears: string[];
  disabled?: boolean;
};
type NumberInputFiltersBlockProps<K extends string> = FiltersBlockCommonProps & {
  type: 'numberInput';
  name: 'price' | 'size';
  secondName: [K, K];
  subTitle: [string, string];
  activeButton: Record<K, string | undefined>;
  onChange: (e: ChangeEvent<HTMLInputElement>, name?: K) => void;
};
type DownPaymentFiltersBlockProps = FiltersBlockCommonProps & {
  type: 'input';
  name: 'downPayment';
  activeButton?: number;
  onClick: (value: number) => void;
};
type KeywordsFiltersBlockProps = FiltersBlockCommonProps & {
  type: 'input';
  name: 'keywords';
  activeButton?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};
type CheckboxFiltersBlockProps = FiltersBlockCommonProps & {
  type: 'checkbox';
  name: 'findListings';
  activeButton: CreatorType;
  onClick: (value: CreatorType, name?: string) => void;
  isMyAgency?: boolean;
  changeMyAgency: (value: boolean) => void;
  myFriends?: boolean;
  changeMyFriends: (value: boolean) => void;
};
type FiltersBlockProps<T extends string | boolean, K extends string> =
  | ButtonFiltersBlockProps<T>
  | DoubleButtonFiltersBlockProps
  | NumberInputFiltersBlockProps<K>
  | DownPaymentFiltersBlockProps
  | KeywordsFiltersBlockProps
  | CheckboxFiltersBlockProps;

export const FiltersBlock = <
  T extends string | boolean = string | boolean,
  K extends string = string,
>(
  props: FiltersBlockProps<T, K>
) => {
  const { name, icon, title, dataQa } = props;

  return (
    <Stack
      direction='column'
      alignItems='flex-start'
      justifyContent='left'
      spacing={1}
      data-qa={`${dataQa}_block`}
    >
      {icon && title && <IconText icon={icon}>{title}</IconText>}
      {props.type === 'button' && (props.propertyTypeTab === 0 || props.propertyTypeTab === 1) && (
        <div className={styles.container}>
          <Tabs
            value={props.propertyTypeTab}
            onChange={(_, val) => props.setPropertyTypeTab?.(val)}
            aria-label='property type tabs'
            className={styles.tabsContainer}
          >
            <Tab
              label='Residential'
              id='property-type-tab-0'
              aria-controls='property-type-tabpanel-0'
              className={styles.tabItem}
            />
            <Tab
              label='Commercial'
              id='property-type-tab-1'
              aria-controls='property-type-tabpanel-1'
              className={styles.tabItem}
            />
          </Tabs>
        </div>
      )}
      {props.type === 'button' && props.onClick && props.size && (
        <FormButtons
          name={name}
          options={props.options}
          onClick={props.onClick}
          activeButton={props.activeButton}
          size={props.size}
          dataQa={dataQa}
        />
      )}
      {props.type === 'doubleButton' && props.onClick && props.size && (
        <Stack spacing={2}>
          <Stack>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 800, marginBottom: '5px', textAlign: 'left' }}
            >
              {props.subTitle[0]}
            </Typography>
            <FormButtons
              name='quarter'
              options={props.options[0].quarters}
              onClick={props.onClick}
              activeButton={props.activeButton}
              size={props.size}
              dataQa={`${dataQa}_${props.subTitle[0]}`}
              disabled={props.disabled}
            />
          </Stack>
          <Stack>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 800, marginBottom: '5px', textAlign: 'left' }}
            >
              {props.subTitle[1]}
            </Typography>
            <FormButtons
              name='year'
              options={props.options[0].years}
              onClick={props.onClick}
              activeButton={props.activeButtonYears}
              size={props.size}
              dataQa={`${dataQa}_${props.subTitle[1]}`}
            />
          </Stack>
        </Stack>
      )}
      {props.type === 'numberInput' && props.subTitle && props.onChange && props.secondName && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          data-qa={`${dataQa}_text_fields_block`}
        >
          <NumericFormat
            sx={{ width: { xs: '100%', sm: '300px' } }}
            customInput={TextField}
            value={props.activeButton[props.secondName[0]]}
            allowNegative={false}
            thousandSeparator=' '
            decimalScale={0}
            placeholder={props.subTitle[0]}
            onKeyDown={(e) => e.stopPropagation()}
            onChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange(e, props.secondName[0])}
            data-qa={`${dataQa}_${props.secondName[0]}`}
          />
          <NumericFormat
            sx={{ width: { xs: '100%', sm: '300px' } }}
            customInput={TextField}
            value={props.activeButton[props.secondName[1]]}
            allowNegative={false}
            thousandSeparator=' '
            decimalScale={0}
            placeholder={props.subTitle[1]}
            onKeyDown={(e) => e.stopPropagation()}
            onChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange(e, props.secondName[1])}
            error={
              Number(props.activeButton[props.secondName[0]]) >
                Number(props.activeButton[props.secondName[1]]) &&
              Number(props.activeButton[props.secondName[1]]) !== 0
            }
            helperText={
              Number(props.activeButton[props.secondName[0]]) >
                Number(props.activeButton[props.secondName[1]]) &&
              Number(props.activeButton[props.secondName[1]]) !== 0
                ? 'max value should be more or equal than min value'
                : ''
            }
            data-qa={`${dataQa}_${props.secondName[1]}`}
          />
        </Stack>
      )}
      {props.type === 'input' && props.name === 'downPayment' && props.onClick && (
        <DownPaymentSearch
          placeholder={title}
          defaultDownPayment={props.activeButton}
          handleChange={props.onClick}
          dataQa={`${dataQa}_${props.type}`}
        />
      )}
      {props.type === 'input' && props.name === 'keywords' && props.onChange && (
        <KeywordSearch
          placeholder={title}
          defaultKeywords={props.activeButton}
          handleChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange(e)}
          dataQa={`${dataQa}_${props.type}`}
        />
      )}
      {props.type === 'checkbox' && props.onClick && (
        <Stack direction={{ xs: 'column' }} justifyContent='left'>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} justifyContent='space-between'>
            <Stack
              direction={'row'}
              alignItems='center'
              justifyContent='left'
              spacing={1}
              data-qa={`${dataQa}_${props.type}`}
            >
              <Checkbox
                icon={<span />}
                checkedIcon={<span className={styles.checked} />}
                style={{ color: '#1650ff' }}
                checked={props.isMyAgency}
                onChange={(e) => {
                  const val = e.target.checked;
                  props.changeMyAgency?.(val);
                }}
              />
              <Typography
                sx={{
                  color: '#1650ff',
                  fontSize: '16px',
                  textAlign: 'left',
                }}
              >
                My agency listings
              </Typography>
            </Stack>
            <Stack
              direction={'row'}
              alignItems='center'
              justifyContent='left'
              spacing={1}
              data-qa={`${dataQa}_${props.type}`}
            >
              <Checkbox
                icon={<span />}
                checkedIcon={<span className={styles.checked} />}
                style={{ color: '#1650ff' }}
                checked={props.myFriends}
                onChange={(e) => {
                  const val = e.target.checked;
                  props.changeMyFriends?.(val);
                }}
              />
              <Typography
                sx={{
                  color: '#1650ff',
                  fontSize: '16px',
                  textAlign: 'left',
                }}
              >
                My friends listings
              </Typography>
            </Stack>
            <Stack>
              <FormSelectWithFetch
                cbSearchBy={searchCreators}
                inputPlaceholder='Search agency / agent'
                handleChange={props.onClick}
                defaultData={props.activeButton}
                isMyAgency={props.isMyAgency}
                changeMyAgency={props.changeMyAgency}
                dataQa={`${dataQa}_select`}
              />
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

const IconText = ({ icon, children }: { icon: ReactElement; children?: string }) => {
  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='left'
      spacing={1}
      sx={{ opacity: 0.6 }}
    >
      {icon}
      <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>{children}</Typography>
    </Stack>
  );
};

const searchCreators = async (searchTerm: string) => {
  if (searchTerm?.length > 2) {
    const response = await usersProvider.getCreatorsByName({ name: searchTerm });
    if (response?.data?.items) {
      return response?.data?.items;
    }
  } else if (searchTerm?.length === 0) {
    return [];
  }
};
